<template>
    <Card :title="$t('labels.products')">
        <template #header-elements>
            <Dropdown
                :title="$t('buttons.actions')"
                :icon="'icon-menu7'"
                :disabled="selectedIds.length === 0"
                class="mr-2"
            >
                <template #menu>
                    <a @click="destroy" class="dropdown-item">
                        <i class="icon-bin2"></i>
                        {{ $t('buttons.delete') }}
                    </a>
                </template>
            </Dropdown>
        </template>
        <DataTable
            :url="table.url"
            :columns="table.columns"
            :orders-props="[{ column: 0, dir: 'desc'}]"
            ref="DataTable"
            :check-box-props="true"
            @changeSelectedIds="newSelectedIds => selectedIds = newSelectedIds"
        >
            <template #rowForCreate>
                <td colspan="3">
                    <Button size="sm"
                        color="teal"
                        :style="{width: '100%'}"
                        :disabled="disabled"
                        @click="focusNextInput()"
                        @keydown:enter="focusNextInput()"
                    >
                        {{ $t('buttons.create') }}
                    </Button>
                </td>
                <td>
                    <Autocomplete
                        @update:model-value="newProduct => selectProductItem(newProduct.product)"
                        :model-value="productItem.product_item_id"
                        :selected-value="productItem.label"
                        option-key="product.uuid"
                        option-value="product.name"
                        url="/management/warehouses/products/availability"
                        ref="Autocomplete"
                        :disabled="disabled"
                        :query-params="{ extra: 1, orders: [{column: 'automatic_arrival', dir: 'desc'}, {column: 'balance', dir: 'desc'}], warehouse_uuids: exitDeclaration.warehouse ? [exitDeclaration.warehouse.uuid] : [], only_with_relation: 0}"
                        :selected-values="$refs.DataTable ? $refs.DataTable.meta.product_uuids : []"
                    >
                        <template #optionItem="{option}">
                            {{ option.product.name }}
                            <ProductAvailabilitySelectOption :option="option"/>
                        </template>
                    </Autocomplete>
                </td>
                <td>
                    <Input
                        ref="ProductItemAmount"
                        @keydown:enter="focusNextInput()"
                        v-model="productItem.amount"
                        :disabled="productItem.product_item_id === null || disabled"
                        type="number"
                    />
                </td>
                <td>
                    <InputSelect
                        v-model="productItem.price"
                        @setOptionValue="selectPriceTemplate"
                        @onShow="fetchPriceTemplatesByProduct"
                        @onHide="productPriceTemplates = {}"
                        @keydown:enter="focusNextInput()"
                        :options="priceTemplates"
                        :disabled="productItem.product_item_id === null || disabled"
                    />
                </td>
                <td>
                    <div class="number-td-block">
                        <div></div>
                        <span>
                            <div>{{ Price.factory(sumWithoutDiscount).toCurrencyString(getCurrencyCodeByUuid(currencyUuid)) }}</div>
                            <slot v-if="currencyUuid !== mainCurrency.uuid && mainCurrency.uuid !== null">
                                <span class="text-muted">{{ Price.factory(sumWithoutDiscount).convertByRate(currencyRate).toCurrencyString(mainCurrency.code) }}</span>
                            </slot>
                        </span>
                    </div>
                </td>
                <td style="max-width: 200px;">
                    <div class="row">
                        <div class="col-lg-7">
                            <Input
                                v-model="productItem.discountSum"
                                type="number"
                                :placeholder="$t('labels.sum')"
                                @update:modelValue="setDiscountSumInCreateForm"
                                :disabled="priceOrAmountEmpty"
                                @keydown:enter="focusNextInput()"
                            />
                        </div>
                        <div class="col-lg-5">
                            <Input
                                v-model="productItem.discountPercent"
                                type="number"
                                placeholder="%"
                                @update:modelValue="setDiscountPercentInCreateForm"
                                :disabled="priceOrAmountEmpty"
                                @keydown:enter="focusNextInput()"
                            />
                        </div>
                    </div>
                </td>
                <td>
                    <div class="number-td-block">
                        <div></div>
                        <span>
                            <div>{{ Price.factory(sumWithDiscount).toCurrencyString(getCurrencyCodeByUuid(currencyUuid)) }}</div>
                            <slot v-if="currencyUuid !== mainCurrency.uuid && mainCurrency.uuid !== null">
                                <span class="text-muted">{{ Price.factory(sumWithDiscount).convertByRate(currencyRate).toCurrencyString(mainCurrency.code) }}</span>
                            </slot>
                        </span>
                    </div>
                </td>
            </template>
            <template v-slot:row-main_image_url="{ row }">
                <div :style="{'text-align': 'center'}">
                    <ImageZoomComponent
                        :url="row.main_image_url !== null ? row.main_image_url : PLACEHOLDER_IMAGE_URL"
                        :alt="row.title"
                        :default-width="50"
                        :default-height="50"
                    />
                </div>
            </template>
            <template v-slot:row-sku="{row}">
                <slot v-if="row.product_item_id !== null && row.sku !== null">
                    <router-link v-if="row?.product_item_uuid" :to="{ name: 'EditProductPage', params: { productUuid: row.product_item_uuid } }" class="long-string" :title="row.title">{{ row.title }}</router-link>
                    <span v-else class="long-string" :title="row.title">{{ row.title }}</span>
                    <span class="text-muted">{{ row.sku }}</span>
                </slot>
            </template>
            <template #row-amount="{row}">
                <div class="number-td-block">
                    <Popover class="unset-popover-margin" @showPopover="fields.amount = row.amount" @hidePopover="hidePopover" v-if="!disabled">
                        <template #body>
                            <FormGroup :label="$t('labels.amount')">
                                <Input v-model="fields.amount" type="number" />
                            </FormGroup>
                            <Button size="sm" @click="edit(row.uuid, 'amount')" color="teal">
                                {{ $t('buttons.edit') }}
                            </Button>
                        </template>
                    </Popover>
                    <div v-else></div>
                    <div>{{ Amount.factory(row.amount).toMeasurementString(row.measurement.name) }}</div>
                </div>
            </template>
            <template #row-price="{row}">
                <div class="number-td-block">
                    <Popover class="unset-popover-margin" @showPopover="fields.price = row.price" @hidePopover="hidePopover" v-if="!disabled">
                        <template #body>
                            <FormGroup :label="$t('labels.price_template')" :required="true">
                                <Select v-model="fields.priceTemplate" @update:modelValue="fetchPriceTemplate(row, $event)" :options="priceTemplates" optionKey="uuid" optionValue="name"/>
                            </FormGroup>
                            <FormGroup :label="$t('labels.price')">
                                <Input v-model="fields.price" type="number" />
                            </FormGroup>
                            <Button size="sm" @click="edit(row.uuid, 'price')" color="teal">
                                {{ $t('buttons.edit') }}
                            </Button>
                        </template>
                    </Popover>
                    <div v-else></div>
                    <span>
                        <div>{{ Price.factory(row.price).toCurrencyString(getCurrencyCodeByUuid(currencyUuid)) }}</div>
                        <slot v-if="currencyUuid !== mainCurrency.uuid && mainCurrency.uuid !== null">
                            <small class="text-muted">{{ Price.factory(row.price).convertByRate(currencyRate).toCurrencyString(mainCurrency.code) }}</small>
                        </slot>
                    </span>
                </div>
            </template>
            <template #row-sum_without_discount="{row}">
                <div class="number-td-block">
                    <div></div>
                    <div>
                        <div>{{ Price.factory(row.sum_without_discount).toCurrencyString(getCurrencyCodeByUuid(currencyUuid)) }}</div>
                        <slot v-if="currencyUuid !== mainCurrency.uuid && mainCurrency.uuid !== null">
                            <small class="text-muted">{{ Price.factory(row.sum_without_discount).convertByRate(currencyRate).toCurrencyString(mainCurrency.code) }}</small>
                        </slot>
                    </div>
                </div>
            </template>
            <template #row-discount="{row}">
                <div class="number-td-block">
                    <Popover class="unset-popover-margin" @showPopover="showPopover(row)" @hidePopover="hidePopover" v-if="!disabled">
                        <template #body>
                            <FormGroup :label="$t('labels.discount_sum')">
                                <Input v-model="discount.discountSum" :disabled="row.price === 0" type="number" @update:modelValue="setDiscountSumInEditForm"/>
                            </FormGroup>
                            <FormGroup :label="$t('labels.percent')">
                                <Input v-model="discount.discountPercent" :disabled="row.price === 0" type="number" @update:modelValue="setDiscountPercentInEditForm"/>
                            </FormGroup>
                            <Button size="sm" @click="edit(row.uuid, 'discount')" color="teal">
                                {{ $t('buttons.edit') }}
                            </Button>
                        </template>
                    </Popover>
                    <div v-else></div>
                    <div style="text-align: end;">
                        <div>{{ Price.factory(row.discount_sum).toCurrencyString(getCurrencyCodeByUuid(currencyUuid)) }}</div>
                        <slot v-if="currencyUuid !== mainCurrency.uuid && mainCurrency.uuid !== null">
                            <span class="text-muted">{{ Price.factory(row.discount_sum).convertByRate(currencyRate).toCurrencyString(mainCurrency.code) }}</span><br/>
                        </slot>
                        <small class="text-muted">{{ (row.discount_percent ?? 0).toFixed(2) }} %</small>
                    </div>
                </div>
            </template>
            <template #row-sum="{row}">
                <div class="number-td-block">
                    <div></div>
                    <div>
                        <div>{{ Price.factory(row.sum).toCurrencyString(getCurrencyCodeByUuid(currencyUuid)) }}</div>
                        <slot v-if="currencyUuid !== mainCurrency.uuid && mainCurrency.uuid !== null">
                            <span class="text-muted">{{ Price.factory(row.sum).convertByRate(currencyRate).toCurrencyString(mainCurrency.code) }}</span>
                        </slot>
                    </div>
                </div>
            </template>
            <template #tFoot>
                <tr>
                    <th colspan="6" style="text-align: end;">{{ $t('labels.total') }}:</th>
                    <th>
                        <div class="number-td-block">
                            <div></div>
                            <div>
                                <div>{{ Price.factory($refs.DataTable?.meta?.product_items_sum_without_discount).toCurrencyString(getCurrencyCodeByUuid(currencyUuid)) }}</div>
                                <slot v-if="currencyUuid !== mainCurrency.uuid && mainCurrency.uuid !== null">
                                    <span class="text-muted">{{ Price.factory($refs.DataTable?.meta?.product_items_sum_without_discount).convertByRate(currencyRate).toCurrencyString(mainCurrency.code) }}</span>
                                </slot>
                            </div>
                        </div>
                    </th>
                    <th>
                        <div class="number-td-block">
                            <div></div>
                            <div>
                                <div>{{ Price.factory($refs.DataTable?.meta?.discount_sum).toCurrencyString(getCurrencyCodeByUuid(currencyUuid)) }}</div>
                                <slot v-if="currencyUuid !== mainCurrency.uuid && mainCurrency.uuid !== null">
                                    <span class="text-muted">{{ Price.factory($refs.DataTable?.meta?.discount_sum).convertByRate(currencyRate).toCurrencyString(mainCurrency.code) }}</span><br/>
                                </slot>
                                <small class="text-muted">{{ ($refs.DataTable?.meta?.discount_percent ?? 0).toFixed(2) }} %</small>
                            </div>
                        </div>
                    </th>
                    <th>
                        <div class="number-td-block">
                            <div></div>
                            <div>
                                <div>{{ Price.factory($refs.DataTable?.meta?.product_items_sum).toCurrencyString(getCurrencyCodeByUuid(currencyUuid)) }}</div>
                                <slot v-if="currencyUuid !== mainCurrency.uuid && mainCurrency.uuid !== null">
                                    <span class="text-muted">{{ Price.factory($refs.DataTable?.meta?.product_items_sum).convertByRate(currencyRate).toCurrencyString(mainCurrency.code) }}</span>
                                </slot>
                            </div>
                        </div>
                    </th>
                </tr>
            </template>
        </DataTable>
    </Card>
</template>

<script>
import Card from '../../../../../components/new/CardComponent.vue';
import DataTable from '../../../../../components/table/DataTableComponent.vue';
import Button from '../../../../../components/new/button/AppButton.vue';
import Popover from '../../../../../components/PopoverComponent.vue';
import Input from '../../../../../components/new/form/Input.vue';
import Select from '../../../../../components/new/form/select/Select.vue';
import Dropdown from '../../../../../components/DropdownComponent.vue';
import Autocomplete from '../../../../../components/new/form/autocomplete/AppAutoComplete.vue';
import FormGroup from '../../../../../components/new/form/group/FormGroup.vue';
import InputSelect from '../../../../../components/new/form/InputSelect.vue';
import Amount from '../../../../../services/Amount';
import Price from '../../../../../services/Price/Price';
import ProductAvailabilitySelectOption from '../../../../../components/new/form/select/ProductAvailabilitySelectOption.vue';
import { httpClient } from '../../../../../services/HttpClient';
import { PLACEHOLDER_IMAGE_URL } from '../../../../../constants';
import { PriceTemplateApi } from '../../../../../spa/api/PriceTemplateApi';
import MessageNotificator from '../../../../../services/MessageNotificator';
import ImageZoomComponent from '../../../../../components/new/image/ImageZoomComponent.vue';
export default {
    name: 'ProductItemsCard',
    components: {
        Card, DataTable, Button, Popover, Input, Select, Autocomplete,
        Dropdown, FormGroup, InputSelect, ProductAvailabilitySelectOption, ImageZoomComponent
    },
    props: {
        exitDeclaration: {
            type: Object,
            required: true
        },
        canModify: {
            type: Boolean,
            default: false
        },
        currencyUuid: {
            type: String,
            required: true
        },
        currencyRate: {
            type: [Number, String],
            required: true
        },
        mainCurrency: {
            type: Object,
            default: () => ({ uuid: null })
        },
        currencies: {
            type: Array,
            required: true
        }
    },
    data() {
        return {
            PLACEHOLDER_IMAGE_URL,
            Price: Price,
            Amount: Amount,
            table: {
                url: `/management/exit-declarations/${this.exitDeclaration.uuid}/exit-declaration-product-items/table`,
                columns: [
                    {
                        data: "id",
                        name: "id",
                        width: '80px',
                        searchable: 1,
                        orderable: true,
                        field: this.$t('table.titles.id')
                    },
                    {
                        data: "main_image_url",
                        name: "main_image_url",
                        width: '120px',
                        searchable: 0,
                        orderable: false,
                        field: this.$t('table.titles.image')
                    },
                    {
                        data: "sku",
                        name: "sku",
                        searchable: 1,
                        width: '400px',
                        orderable: true,
                        field: this.$t('table.titles.name')
                    },
                    {
                        data: "amount",
                        name: "amount",
                        searchable: 0,
                        width: '150px',
                        orderable: true,
                        field: this.$t('table.titles.amount')
                    },
                    {
                        data: "price",
                        name: "price",
                        searchable: 0,
                        width: '150px',
                        orderable: true,
                        field: this.$t('table.titles.price_per_unit')
                    },
                    {
                        data: "sum_without_discount",
                        name: "sum_without_discount",
                        searchable: 0,
                        orderable: true,
                        width: '150px',
                        field: this.$t('table.titles.sum_without_discount')
                    },
                    {
                        data: "discount",
                        name: "discount",
                        searchable: 0,
                        orderable: true,
                        width: '150px',
                        field: this.$t('table.titles.discount')
                    },
                    {
                        data: "sum",
                        name: "sum",
                        searchable: 0,
                        orderable: true,
                        width: '150px',
                        field: this.$t('table.titles.sum')
                    }
                ]
            },
            fields: {
                price: 0,
                priceTemplate: null,
                amount: 0
            },
            discount: {
                discountSum: 0,
                discountPercent: 0
            },
            productItem: {
                product_item_id: null,
                label: '',
                price: '',
                amount: '',
                discountSum: '',
                priceTemplates: {},
                discountPercent: ''
            },
            selectedIds: [],
            productPriceTemplates: {},
            priceTemplates: []
        }
    },
    methods: {
        destroy() {
            if (!this.disabled) {
                new MessageNotificator().confirm(
                    `${this.$t('alerts.confirm_for_delete', { model: this.$t('labels.product_item') })}`,
                    () => {
                        httpClient.delete(`/management/exit-declarations/${this.exitDeclaration.uuid}/product-items`, {
                            product_item_ids: this.selectedIds
                        }).then(() => {
                            this.selectedIds = [];
                            this.$refs.DataTable.load();
                        })
                    }
                )
            }
        },
        edit(productItemUuid, property) {
            let request = {}
            switch (property) {
                case 'amount':
                    request = { amount: this.fields.amount }
                    break
                case 'price':
                    request = { price: this.fields.price }
                    break
                case 'discount':
                    request = {
                        discount_sum: this.discount.discountSum,
                        discount_percent: this.discount.discountPercent
                    }
                    break
            }

            httpClient.put(`/management/exit-declarations/${this.exitDeclaration.uuid}/product-items/${productItemUuid}`, request).then(() => {
                new MessageNotificator().success(this.$t('alerts.success'));
                this.$refs.DataTable.load();
            })
        },
        showPopover(row) {
            this.fields.amount = row.amount;
            this.fields.price = row.price;
            this.discount.discountSum = row.discount_sum;
            this.discount.discountPercent = row.discount_percent;
        },
        hidePopover() {
            this.fields = { price: 0, priceTemplate: null, amount: 0 }
            this.discount = { discountSum: 0, discountPercent: 0 }
        },
        selectProductItem(productItem) {
            new Promise((resolve) => {
                this.productItem.product_item_id = productItem.id;
                this.productItem.label = productItem.name;
                this.productItem.priceTemplates = productItem.price_templates;
                resolve();
            }).then(() => {
                this.$refs.ProductItemAmount.focus();
            });
        },
        selectPriceTemplate(uuid) {
            this.productItem.price = this.productPriceTemplates[uuid] !== undefined
                ? this.productPriceTemplates[uuid]
                : 0
        },
        focusNextInput() {
            let request = { product_item_id: this.productItem.product_item_id }

            this.productItem.amount !== '' ? request.amount = this.productItem.amount : null
            this.productItem.price !== '' ? request.price = this.productItem.price : null
            this.productItem.discountSum !== '' ? request.discount_sum = this.productItem.discountSum : null
            this.productItem.discountPercent !== '' ? request.discount_percent = this.productItem.discountPercent : null

            httpClient.post(`/management/exit-declarations/${this.exitDeclaration.uuid}/product-items`, request).then(() => {
                new MessageNotificator().success(
                    this.$t('alerts.added', { model: this.$t('labels.product_item') })
                );
                this.$refs.DataTable.load();
                this.productItem.product_item_id = null;
                this.productItem.label = '';
                this.productItem.amount = '';
                this.productItem.price = '';
                this.productItem.discountSum = '';
                this.productItem.discountPercent = '';
            })
        },
        fetchPriceTemplate(productItem, priceTemplate) {
            httpClient.get(`/catalog/product-items/${productItem.product_item_uuid}`).then((response) => {
                this.fields.price = response.data.data.price_templates[priceTemplate] ?? 0
            })
        },
        getCurrencyCodeByUuid(currencyUuid) {
            if (!currencyUuid) return
            return this.currencies.find(currency => currency.uuid === currencyUuid)?.code ?? ''
        },
        fetchPriceTemplatesByProduct() {
            httpClient.get('/catalog/product-items', {
                ids: [this.productItem.product_item_id]
            }).then((response) => {
                this.productPriceTemplates = response.data.data[0].price_templates
            })
        },
        setDiscountSumInEditForm(incomingDiscountSum) {
            let { discountSum, discountPercent } = this.calculateDiscountBySum(incomingDiscountSum, this.fields.amount, this.fields.price)
            this.discount.discountSum = discountSum;
            this.discount.discountPercent = discountPercent;
        },
        setDiscountPercentInEditForm(incomingDiscountPercent) {
            let { discountSum, discountPercent } = this.calculateDiscountByPercent(incomingDiscountPercent, this.fields.amount, this.fields.price)
            this.discount.discountSum = discountSum;
            this.discount.discountPercent = discountPercent;
        },
        setDiscountSumInCreateForm(incomingDiscountSum) {
            let { discountSum, discountPercent } = this.calculateDiscountBySum(incomingDiscountSum, this.productItem.amount, this.productItem.price)
            this.productItem.discountSum = discountSum;
            this.productItem.discountPercent = discountPercent;
        },
        setDiscountPercentInCreateForm(incomingDiscountPercent) {
            let { discountSum, discountPercent } = this.calculateDiscountByPercent(incomingDiscountPercent, this.productItem.amount, this.productItem.price)
            this.productItem.discountSum = discountSum;
            this.productItem.discountPercent = discountPercent;
        },
        calculateDiscountBySum(incomingDiscountSum, amount, price) {
            let sum = amount * price;
            let discountSum = this.getValidValueByRange(incomingDiscountSum, sum);
            let discountPercent = ((discountSum / sum) * 100).toFixed(2);
            return { discountSum, discountPercent };
        },
        calculateDiscountByPercent(incomingDiscountPercent, amount, price) {
            let sum = amount * price;
            let discountPercent = this.getValidValueByRange(incomingDiscountPercent, 100);
            let discountSum = (((sum) * discountPercent) / 100).toFixed(2);
            return { discountSum, discountPercent };
        },
        getValidValueByRange(value, max) {
            if (value > max) return max;
            if (value < 0) return 0;
            return value;
        }
    },
    computed: {
        disabled() {
            return this.canModify
        },
        priceOrAmountEmpty() {
            return this.productItem.price.length === 0 || this.productItem.amount.length === 0
        },
        sumWithoutDiscount() {
            return (this.productItem.amount * this.productItem.price).toFixed(2)
        },
        sumWithDiscount() {
            return (this.productItem.amount * this.productItem.price - this.productItem.discountSum).toFixed(2)
        }
    },
    mounted() {
        PriceTemplateApi.findAll(this.$route.params.projectUuid, { limit: 0 }).then((response) => {
            this.priceTemplates = response
        })
    }
}
</script>
<style scoped>
.long-string {
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    white-space: normal;
    word-break: break-word;
}
.table-image {
    max-width: 50px;
    max-height: 50px;
}
.number-td-block {
    display: flex;
    justify-content: space-between;
    align-content: center;
    flex-direction: row;
    align-items: center;
}
.unset-popover-margin {
    margin: unset;
}
</style>
